<script setup lang="ts">
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import {
  ArrowRightStartOnRectangleIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  MoonIcon,
  SunIcon,
  UserCircleIcon,
} from '@heroicons/vue/24/outline';
import { useAuthStore } from '@/stores/auth.store';
import type { ThemePreference } from '@/common/Theme';
import { config } from '@/config';

const props = defineProps<{ theme: ThemePreference; localDates: boolean }>();
defineEmits<{ 'toggle-theme': []; 'update:localDates': [enabled: boolean] }>();

const authStore = useAuthStore();
const router = useRouter();

// Lands on the sign-in screen rather than staying put: every real route is
// requiresAuth, so remaining on one would re-enter the flow that was just
// left. No returnTo — signing out is not a detour on the way somewhere.
async function logoutHandler() {
  if (await authStore.logout()) {
    router.push({ path: '/login', query: { reason: 'signed-out' } });
  }
}

// Three states need naming, not just an icon — "auto" is invisible otherwise,
// and a user cannot tell it from whichever of light/dark it resolved to.
const THEME_LABELS: Record<ThemePreference, string> = {
  auto: 'Auto',
  light: 'Light',
  dark: 'Dark',
};

const themeLabel = computed(() => THEME_LABELS[props.theme]);

// API-key auth is only reachable by pasting credentials into /config, so the
// link has to stay reachable even in prod for those sessions.
const showConfigLink = computed(() => config.env !== 'prod' || config.authType === 'api-key');
</script>

<template>
  <Menu as="div" class="relative">
    <MenuButton
      type="button"
      class="fixed top-4 right-4 z-40 flex size-9 cursor-pointer items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
      data-test="btn-account-menu"
    >
      <span class="sr-only">Account menu</span>
      <UserCircleIcon class="size-7" aria-hidden="true" />
    </MenuButton>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems
        class="fixed top-16 right-4 z-40 w-64 rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/5 focus:outline-none"
      >
        <div class="flex items-center justify-between px-2 py-2">
          <span class="text-sm font-semibold text-gray-700">Theme</span>
          <button
            type="button"
            class="flex cursor-pointer items-center gap-x-1.5 rounded-md px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-100"
            data-test="btn-theme-toggle"
            :aria-label="`Theme: ${themeLabel}. Change theme.`"
            @click="$emit('toggle-theme')"
          >
            <ComputerDesktopIcon v-if="theme === 'auto'" class="size-5" aria-hidden="true" />
            <SunIcon v-else-if="theme === 'light'" class="size-5" aria-hidden="true" />
            <MoonIcon v-else class="size-5" aria-hidden="true" />
            <span data-test="theme-label">{{ themeLabel }}</span>
          </button>
        </div>
        <label
          class="flex cursor-pointer items-center justify-between px-2 py-2"
          data-test="local-dates"
        >
          <span class="text-sm font-semibold text-gray-700">Local dates</span>
          <input
            type="checkbox"
            class="size-4 cursor-pointer accent-indigo-600"
            data-test="checkbox-local-dates"
            :checked="localDates"
            @change="$emit('update:localDates', ($event.target as HTMLInputElement).checked)"
          />
        </label>

        <div class="my-1 border-t border-gray-100" />

        <MenuItem v-if="showConfigLink" v-slot="{ active }">
          <RouterLink
            to="/config"
            data-test="link-config"
            :class="[
              active ? 'bg-gray-100 text-black' : 'text-gray-700',
              'flex items-center gap-x-2 rounded-md px-2 py-2 text-sm',
            ]"
          >
            <Cog6ToothIcon class="size-5" aria-hidden="true" />
            Config
          </RouterLink>
        </MenuItem>

        <!-- Does not navigate: Larimar has no logged-out destination yet. -->
        <MenuItem v-if="authStore.status === 'authenticated'" v-slot="{ active }">
          <button
            type="button"
            class="flex w-full cursor-pointer items-center gap-x-2 rounded-md px-2 py-2 text-sm"
            :class="active ? 'bg-gray-100 text-black' : 'text-gray-700'"
            data-test="btn-logout"
            @click="logoutHandler"
          >
            <ArrowRightStartOnRectangleIcon class="size-5" aria-hidden="true" />
            Log out
          </button>
        </MenuItem>
      </MenuItems>
    </transition>
  </Menu>
</template>
